import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi, tutorApi } from '../services/api';
import ChangePassword from '../components/ChangePassword';
import '../assets/LoginPage.css';
import logo from '../assets/images/jj-tutorial-logo.png.png';
import axios from 'axios';

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '0x4AAAAAAEFdlJgLIXja7-Cm';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [shakeForm, setShakeForm] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Logging in...');
  const [isWakingUp, setIsWakingUp] = useState(false);
  const [failedAttempts, setFailedAttempts] = useState(() => {
    return parseInt(sessionStorage.getItem('loginFailedAttempts') || '0');
  });
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [cooldown, setCooldown] = useState(() => {
    const savedCooldownEnd = parseInt(sessionStorage.getItem('loginCooldownEnd') || '0');
    const remaining = Math.ceil((savedCooldownEnd - Date.now()) / 1000);
    return remaining > 0 ? remaining : 0;
  });
  const cooldownRef = useRef(null);
  const turnstileRef = useRef(null);
  const turnstileWidgetId = useRef(null);
  const navigate = useNavigate();

  const getApiBase = () => {
    if (process.env.REACT_APP_API_URL) {
      return process.env.REACT_APP_API_URL.replace('/api', '');
    }
    if (window.location.hostname === 'portal.jjtutorialsg.com') {
      return 'https://jjt-tutor-portal.onrender.com';
    }
    if (window.location.hostname === 'demo.jjtutorialsg.com') {
      return 'https://jjt-tutor-portal-backend-demo.onrender.com';
    }
    return 'http://localhost:5000';
  };

  const isDemoSite = window.location.hostname === 'demo.jjtutorialsg.com' ||
    (process.env.REACT_APP_API_URL && process.env.REACT_APP_API_URL.includes('demo'));

  const handleDemoLogin = async (demoEmail) => {
    setEmail(demoEmail);
    setPassword('demo1234');
    setError('');
    setLoading(true);
    setLoadingMessage('Logging in to demo...');
    setIsWakingUp(false);

    try {
      let response;
      try {
        response = await adminApi.login(demoEmail, 'demo1234');
      } catch (firstErr) {
        const isColdStart = firstErr.code === 'ECONNABORTED'
          || firstErr.message.includes('timeout')
          || firstErr.message.includes('Network Error')
          || !firstErr.response;

        if (!isColdStart) throw firstErr;

        const awake = await waitForServer(() => {
          setIsWakingUp(true);
          setLoadingMessage('Server is waking up... Please wait (up to 2 min)');
        });

        if (!awake) {
          setError('Server took too long to wake up. Please try again.');
          return;
        }

        setIsWakingUp(false);
        setLoadingMessage('Logging in to demo...');
        response = await adminApi.login(demoEmail, 'demo1234');
      }

      const { token, role, _id, name, email: userEmail, mustChangePassword, isCoFounder, financeICSettings } = response.data;
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify({
        _id, name, email: userEmail, role, mustChangePassword,
        isCoFounder: isCoFounder || false,
        financeICSettings: financeICSettings || { isEligible: false, isActive: false },
      }));

      // Signal a fresh login so the SGT timezone notice shows once after login
      sessionStorage.setItem('tz_login_check', '1');
      sessionStorage.removeItem('tz_notice_dismissed');

      if (role === 'admin' || role === 'finance') {
        navigate('/admin');
      } else if (role === 'tutor') {
        navigate('/tutor');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Demo login failed. Please try again.');
      setShakeForm(true);
      setTimeout(() => setShakeForm(false), 500);
    } finally {
      setLoading(false);
      setIsWakingUp(false);
    }
  };

  // Poll server until it's awake, returns true when ready
  const waitForServer = async (onWaking) => {
    const apiBase = getApiBase();
    const maxAttempts = 24; // ~2 minutes
    for (let i = 0; i < maxAttempts; i++) {
      try {
        await axios.get(`${apiBase}/api/ping`, { timeout: 8000 });
        return true;
      } catch {
        if (i === 1) onWaking(); // show waking message after 2nd fail
        await new Promise(r => setTimeout(r, 5000));
      }
    }
    return false;
  };

  // Pre-warm the server when component mounts
  useEffect(() => {
    const apiBase = getApiBase();
    axios.get(`${apiBase}/api/ping`, { timeout: 5000 }).catch(() => {});
  }, []);

  // Load Turnstile script when needed (after failed attempts)
  useEffect(() => {
    if (failedAttempts < 3) return;
    if (document.getElementById('turnstile-script')) return;

    const script = document.createElement('script');
    script.id = 'turnstile-script';
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onTurnstileLoad&render=explicit';
    script.async = true;
    document.head.appendChild(script);

    window.onTurnstileLoad = () => {
      if (turnstileRef.current && window.turnstile) {
        turnstileWidgetId.current = window.turnstile.render(turnstileRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: (token) => setTurnstileToken(token),
          'expired-callback': () => setTurnstileToken(null),
          theme: 'light',
        });
      }
    };

    return () => {
      delete window.onTurnstileLoad;
    };
  }, [failedAttempts]);

  // Render Turnstile widget when ref becomes available after failedAttempts increases
  useEffect(() => {
    if (failedAttempts >= 3 && turnstileRef.current && window.turnstile && !turnstileWidgetId.current) {
      turnstileWidgetId.current = window.turnstile.render(turnstileRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: (token) => setTurnstileToken(token),
        'expired-callback': () => setTurnstileToken(null),
        theme: 'light',
      });
    }
  }, [failedAttempts]);

  const resetTurnstile = useCallback(() => {
    setTurnstileToken(null);
    if (window.turnstile && turnstileWidgetId.current !== null) {
      window.turnstile.reset(turnstileWidgetId.current);
    }
  }, []);

  // Cooldown timer — ticks down every second
  useEffect(() => {
    if (cooldown <= 0) return;
    cooldownRef.current = setInterval(() => {
      setCooldown(prev => {
        if (prev <= 1) {
          clearInterval(cooldownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(cooldownRef.current);
  }, [cooldown]);

  const handleSubmit = async (e) => {
    // Prevent form submission and page refresh
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    setLoading(true);
    setError('');
    setLoadingMessage('Logging in...');
    setIsWakingUp(false);

    // Normalize email: trim whitespace and convert to lowercase
    const normalizedEmail = email.trim().toLowerCase();

    // Basic validation
    if (!normalizedEmail || !password) {
      setError('Please enter both email and password');
      setLoading(false);
      return;
    }

    // If CAPTCHA is required (3+ failed attempts), enforce it
    if (failedAttempts >= 3 && !turnstileToken) {
      setError('Please complete the security check');
      setLoading(false);
      return;
    }

    try {
      // Attempt login; if it fails due to network/cold start, wait for server then retry once
      let response;
      const loginPayload = { email: normalizedEmail, password };
      if (turnstileToken) {
        loginPayload.turnstileToken = turnstileToken;
      }

      try {
        response = await adminApi.login(normalizedEmail, password, turnstileToken);
      } catch (firstErr) {
        const isColdStart = firstErr.code === 'ECONNABORTED' 
          || firstErr.message.includes('timeout') 
          || firstErr.message.includes('Network Error')
          || !firstErr.response;

        if (!isColdStart) throw firstErr; // real error (wrong password etc), rethrow

        // Server is sleeping — poll until awake then retry
        const awake = await waitForServer(() => {
          setIsWakingUp(true);
          setLoadingMessage('Server is waking up... Please wait (up to 2 min)');
        });

        if (!awake) {
          setError('Server took too long to wake up. Please try again in a moment.');
          setShakeForm(true);
          setTimeout(() => setShakeForm(false), 500);
          return;
        }

        setIsWakingUp(false);
        setLoadingMessage('Logging in...');
        response = await adminApi.login(normalizedEmail, password, turnstileToken); // retry
      }
      
      const { token, role, _id, name, email: userEmail, mustChangePassword, isCoFounder, financeICSettings } = response.data;

      // Clear failed attempt tracking on successful login
      sessionStorage.removeItem('loginFailedAttempts');
      sessionStorage.removeItem('loginCooldownEnd');

      // Store JWT token in localStorage
      localStorage.setItem('token', token);
      
      // Store user info (including Finance IC settings)
      localStorage.setItem('user', JSON.stringify({ 
        _id, 
        name, 
        email: userEmail, 
        role, 
        mustChangePassword,
        isCoFounder: isCoFounder || false,
        financeICSettings: financeICSettings || { isEligible: false, isActive: false }
      }));

      // Signal that a fresh login just happened so the SGT timezone notice
      // can show once (only after login, not on every page load/refresh).
      sessionStorage.setItem('tz_login_check', '1');
      sessionStorage.removeItem('tz_notice_dismissed');

      // Check if password change is required
      if (mustChangePassword) {
        setUserRole(role);
        setShowChangePassword(true);
      } else {
        // Redirect to appropriate dashboard based on role
        if (role === 'admin' || role === 'finance') {
          navigate('/admin'); // Both admin and finance use admin portal
        } else if (role === 'tutor') {
          navigate('/tutor');
        } else {
          setError('Invalid user role');
        }
      }
    } catch (err) {
      // Display error messages for invalid credentials
      const errorMessage = err.response?.data?.message || 'Invalid email or password. Please try again.';
      setError(errorMessage);
      
      // Track failed attempts for CAPTCHA trigger
      const newAttempts = failedAttempts + 1;
      setFailedAttempts(newAttempts);
      sessionStorage.setItem('loginFailedAttempts', newAttempts.toString());
      
      // Progressive cooldown on the button (frontend-side)
      // 1-2 attempts: no cooldown, 3-4: 5s, 5-6: 15s, 7+: 30s
      let newCooldown = 0;
      if (newAttempts >= 7) {
        newCooldown = 30;
      } else if (newAttempts >= 5) {
        newCooldown = 15;
      } else if (newAttempts >= 3) {
        newCooldown = 5;
      }
      if (newCooldown > 0) {
        setCooldown(newCooldown);
        sessionStorage.setItem('loginCooldownEnd', (Date.now() + newCooldown * 1000).toString());
      }
      
      // Reset Turnstile so user can re-verify on next attempt
      resetTurnstile();
      
      // Trigger shake animation
      setShakeForm(true);
      setTimeout(() => setShakeForm(false), 500);
      
      // DO NOT clear the inputs - they should remain
      // setEmail(''); // REMOVED
      // setPassword(''); // REMOVED
    } finally {
      setLoading(false);
      setIsWakingUp(false);
    }
  };

  const handlePasswordChanged = () => {
    // After password is changed, redirect to appropriate dashboard
    if (userRole === 'admin' || userRole === 'finance') {
      navigate('/admin'); // Both admin and finance use admin portal
    } else if (userRole === 'tutor') {
      navigate('/tutor');
    }
  };

  return (
    <div className="login-container">
      {showChangePassword && (
        <ChangePassword onPasswordChanged={handlePasswordChanged} />
      )}
      
      <div className={`login-form ${shakeForm ? 'shake-form' : ''}`}>
        <div className="logo">
          <img src={logo} alt="JJ Tutorial" className="login-logo" />
          <h1>JJ Tutorial Singapore</h1>
          <p>Tutor Portal</p>
        </div>
        
        {error && <div className="error-message">{error}</div>}
        
        {isWakingUp && (
          <div className="info-message">
            <div className="spinner-small"></div>
            <span>Server is waking up from sleep mode. Please wait...</span>
          </div>
        )}
        
        <form onSubmit={(e) => { e.preventDefault(); handleSubmit(e); }} autoComplete="on">
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck="false"
              required
            />
          </div>
          
          <div className="form-group password-group">
            <label htmlFor="password">Password</label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                    <line x1="1" y1="1" x2="23" y2="23"></line>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                )}
              </button>
            </div>
          </div>
          
          {failedAttempts >= 3 && (
            <div className="turnstile-wrapper">
              <div ref={turnstileRef}></div>
            </div>
          )}
          
          {failedAttempts >= 3 && (
            <div className="login-warning">
              ⚠️ {failedAttempts} failed attempt{failedAttempts > 1 ? 's' : ''}. 
              {failedAttempts >= 5 ? ' Too many attempts — please wait.' : ' Please verify you are human.'}
            </div>
          )}
          
          <button type="submit" disabled={loading || cooldown > 0}>
            {loading ? loadingMessage : cooldown > 0 ? `Wait ${cooldown}s` : 'Login'}
          </button>
        </form>

        {isDemoSite && (
          <div className="demo-section">
            <div className="demo-divider">
              <span>or try a demo account</span>
            </div>
            <div className="demo-buttons">
              <button
                type="button"
                className="demo-btn demo-btn-admin"
                onClick={() => handleDemoLogin('admin@demo.com')}
                disabled={loading}
              >
                👤 Login as Admin
              </button>
              <button
                type="button"
                className="demo-btn demo-btn-tutor"
                onClick={() => handleDemoLogin('alice@demo.com')}
                disabled={loading}
              >
                🎓 Login as Tutor
              </button>
            </div>
            <p className="demo-note">Demo data only — no real information</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoginPage;